import type { Seat } from '../../../shared/domain.ts';
import { NameEditor } from '../auth/NameEditor.tsx';
import { Board } from '../match/Board.tsx';
import { ChatPanel } from '../match/ChatPanel.tsx';
import type { MatchView, WaitingIn } from '../match/useMatch.ts';

export interface WaitingRoomProps {
  readonly waiting: WaitingIn;
  readonly gameName: string;
  readonly onLeave: () => void;
  /** Everything on screen while waiting: the watched board and the room's chat. */
  readonly view: MatchView;
  /** The name the host reads off their queue, so it is editable from here. */
  readonly yourName: string;
  readonly onRename: (name: string) => void;
  readonly onSay: (text: string) => void;
  readonly yourUserId: string | null;
}

function ordinal(position: number): string {
  // 11th–13th break the last-digit rule, which is exactly the range a busy
  // stream queue reaches.
  const teens = position % 100;
  if (teens >= 11 && teens <= 13) return `${position}th`;
  switch (position % 10) {
    case 1:
      return `${position}st`;
    case 2:
      return `${position}nd`;
    case 3:
      return `${position}rd`;
    default:
      return `${position}th`;
  }
}

function nameAt(view: MatchView, seat: Seat): string {
  return view.seats?.[seat]?.displayName ?? `Seat ${seat}`;
}

/** "Ana vs Bo", in the seat order the game itself declares. */
function matchTitle(view: MatchView): string {
  return view.spec.rules.seats.map((seat) => nameAt(view, seat)).join(' vs ');
}

/**
 * How the watched game finished, named rather than coded.
 *
 * A waiter is watching to size up the player they may face next, so who won and
 * how is the whole point of the line.
 */
function resultLine(view: MatchView): string | null {
  if (!view.result) return null;
  const { result, reason } = view.result;
  if (result === 'draw') return 'It ended in a draw.';

  const seats = view.spec.rules.seats;
  const winner = result === 'seat1_win' ? seats[0] : seats[1];
  if (winner === undefined) return 'That game is over.';

  const name = nameAt(view, winner);
  switch (reason) {
    case 'resign':
      return `${name} won by resignation.`;
    case 'abandon':
    case 'timeout':
      return `${name} won: their opponent left.`;
    default:
      return `${name} won.`;
  }
}

/**
 * The queue as a place to be, rather than a number to stare at.
 *
 * Someone who scanned a code off a stream lands here and may wait through
 * several games. Three things keep them: a truthful place in line, the game
 * they are waiting behind, and the room's conversation. The name they will be
 * announced by is editable here too, because this screen is where they find out
 * the host is about to read it out.
 *
 * Only the position is a live region. The board is watched, not narrated: a
 * spectator can see the pieces land, and announcing every move of somebody
 * else's game would talk over the one thing they are here to hear.
 */
export function WaitingRoom({
  waiting,
  gameName,
  onLeave,
  view,
  yourName,
  onRename,
  onSay,
  yourUserId,
}: WaitingRoomProps) {
  const next = waiting.position === 1;
  const live = view.spectating && view.seats !== null;
  const finished = resultLine(view);

  return (
    <section className="waitingroom" aria-labelledby="waiting-heading">
      <header className="waitingroom__intro">
        <h2 id="waiting-heading" className="waitingroom__heading">
          You&rsquo;re in {waiting.hostName}&rsquo;s queue
        </h2>
        <p className="waitingroom__game">Playing {gameName}</p>

        <p className="waitingroom__position" aria-live="polite">
          <span className="waitingroom__rank" aria-hidden="true">
            {waiting.position}
          </span>
          {next ? "You're next up." : `You're ${ordinal(waiting.position)} in line.`}
        </p>
      </header>

      <div className="waitingroom__layout">
        {/*
          The board leads on a wide screen and comes first on a narrow one: it
          is the only part of this screen that changes on its own, and it is
          what makes the wait watchable.
        */}
        <div className="waitingroom__stage">
          {live ? (
            <>
              <h3 className="waitingroom__stage-heading">{matchTitle(view)}</h3>
              <p className="waitingroom__stage-status">
                {finished ?? `${waiting.hostName} is playing now. You are watching.`}
              </p>
              <Board
                spec={view.spec}
                board={view.board}
                yourSeat={null}
                legalColumns={[]}
                pendingColumn={null}
                lastMoveIndex={view.lastMoveIndex}
                winningLine={view.winningLine}
                interactive={false}
                onDrop={() => undefined}
                /*
                  No column buttons. There is nothing here for a spectator to
                  drop, and a row of disabled buttons would be dead tab stops
                  between them and the chat.
                */
                columnControls={false}
              />
            </>
          ) : (
            /* Honest empty state: no game is running, and none is pretended. */
            <div className="waitingroom__stage-empty">
              <p className="waitingroom__stage-heading">No game on yet</p>
              <p className="waitingroom__stage-status">
                {waiting.hostName} decides who plays next. Keep this page open, your game starts on
                its own.
              </p>
            </div>
          )}
        </div>

        <div className="waitingroom__side">
          {/* This is the name the host reads off their queue, so it is fixable
              here rather than only back on the screen before this one. */}
          <NameEditor currentName={yourName} onRename={onRename} />

          <ChatPanel
            messages={view.chat}
            yourUserId={yourUserId}
            onSend={onSay}
            disabled={view.connection === 'reconnecting'}
            heading="Room chat"
            emptyHint="Nothing said yet. Say hello."
            inputLabel="Message the room"
          />
        </div>
      </div>

      <footer className="waitingroom__actions">
        <button type="button" className="button button--quiet" onClick={onLeave}>
          Leave queue
        </button>
      </footer>
    </section>
  );
}
